import {reporter} from 'common/report/error-reporting';

import {StorageManager, StorageSetOptions} from './types';
import {isAllowedByGdpr, isAllowedNamespacePrefix, validateKeyValue} from './util';

// TODO: kill localStorage monkeypatching
export class GdprStorageManager implements StorageManager {
  private declare persistance: Storage;
  private hasWarnedSetFailure = false;

  constructor(persistance: Storage) {
    this.persistance = persistance;
  }
  public setItem(key: string, value: string, options?: StorageSetOptions): Promise<boolean> | boolean {
    if (options?.unsafe) {
      // when options.unsafe=true, skip key/value validation and return synchronously
      return this.unsafeSetItem(key, value);
    }
    return this.asyncSetItem(key, value);
  }

  public getItem(key: string): null | string {
    return this.persistance.getItem(key);
  }

  public removeItem(key: string): void {
    this.persistance.removeItem(key);
  }

  public addStorageEventListener(callback: () => any): void {
    window.addEventListener(`storage`, callback);
  }

  public removeStorageEventListener(callback: () => any): void {
    window.removeEventListener(`storage`, callback);
  }

  private async asyncSetItem(key: string, value: string): Promise<boolean> {
    validateKeyValue(key, value);

    if (!isAllowedByGdpr(key) && !isAllowedNamespacePrefix(key)) {
      if (!window.gdprData) {
        this.warnGdprUnavailable();
        return false;
      }

      await window.gdprData.waitUntilReady();

      if (!window.gdprData.allowStorage) {
        this.warnGdprDisabled();
        return false;
      }
    }

    return this.trySetItem(key, value);
  }

  private unsafeSetItem(key: string, value: string): boolean {
    // skip key/value validation
    if (!isAllowedByGdpr(key) && !isAllowedNamespacePrefix(key)) {
      if (!window.gdprData) {
        this.warnGdprUnavailable();
        return false;
      }

      // don't wait for GDPR readiness
      if (!window.gdprData.isReady) {
        this.warnGdprNotReady();
        return false;
      }

      if (!window.gdprData.allowStorage) {
        this.warnGdprDisabled();
        return false;
      }
    }

    return this.trySetItem(key, value);
  }

  private trySetItem(key: string, value: string): boolean {
    try {
      this.persistance.setItem(key, value);
    } catch (err) {
      if (!this.hasWarnedSetFailure) {
        reporter.warn(err as DOMException, `Failed to set ${key}`);
        this.hasWarnedSetFailure = true;
      }
      return false;
    }
    return true;
  }

  private warnGdprUnavailable(): void {
    console.warn(`storage disabled because GDPR status cannot be determined`);
  }

  private warnGdprNotReady(): void {
    console.warn(`storage disabled because GDPR status is not yet known`);
  }

  private warnGdprDisabled(): void {
    console.warn(`storage disabled due to GDPR opt out`);
  }
}
